/**
 * Single-product viewer setup. Resolves a SKU into configurator state for
 * `/#view?article=<SKU>`. Returns an error message string on failure or
 * `undefined` on success.
 *
 * See:
 *   - docs/adr/0001-viewer-url-contract-is-raw-sku.md
 *   - docs/adr/0002-viewer-mode-extends-configurator-data-model.md
 *   - gitlab.animech.com/clients/grythyttan/-/issues/4
 */
import { Material } from "@animech/axiom-format/src/definitions/definition-bindings";
import { lookupSkuCombo, ProductType } from "src/core/articles/sku-lookup";
import { Configurator } from "src/utility";
import { root } from "src/definitions";
import { tableOptions } from "src/definitions/table-data";
import {
	BenchType,
	ChairType,
	SeatingType,
	seatingTypeUniverse,
	SofaType,
	TableType,
	ViewerMode,
	tableTypeUniverse,
	viewerModeForSeating,
} from "src/definitions/type-definitions";

function isTable(productType: ProductType): productType is TableType {
	return (tableTypeUniverse as string[]).includes(productType);
}

function setSeating(
	configurator: Configurator,
	productType: SeatingType,
	wood: Material | undefined,
	metal: Material,
	viewerMode: ViewerMode,
): void {
	configurator.set(root.viewerMode, viewerMode);
	// chairConfiguration's run() hides chair[1..N] when viewerMode !== "off";
	// we own chair[0] entirely. Material weld/endcap/screws are derived by the
	// chair.$ template rules from chairMaterialWood/Metal — we only write the
	// source-of-truth fields.
	//
	// Atomic value+domain write: chairConfiguration narrowed chair[0].chairType
	// domain to long-side-allowed seatings for the default table before viewer
	// mode kicked in. That narrow domain excludes some products (e.g.
	// Bench-Libelle is not allowed on Table-B31-170 long sides). Widen back to
	// the full seating universe in the same write so any catalog SKU lands.
	configurator.set(root.chair[0].chairType, {
		value: productType,
		domain: seatingTypeUniverse,
	});
	configurator.set(root.chair[0].chairMaterialWood, wood);
	configurator.set(root.chair[0].chairMaterialMetal, metal);
	configurator.set(root.chair[0].visible, true);
	configurator.set(root.chair[0].count, 1);
	configurator.set(root.chair[0].sideId, "top");
	configurator.set(root.chair[0].pos.x, 0);
	configurator.set(root.chair[0].pos.y, 0);
	configurator.set(root.chair[0].pos.z, 0);
	configurator.set(root.chair[0].rot.x, 0);
	configurator.set(root.chair[0].rot.y, 0);
	configurator.set(root.chair[0].rot.z, 0);
}

function setTable(
	configurator: Configurator,
	productType: TableType,
	wood: Material | undefined,
	metal: Material,
): void {
	configurator.set(root.viewerMode, "single-table");
	configurator.set(root.tableType, productType);
	// Atomic value+domain writes — see comment on the equivalent block in
	// setSeating for chairType. The cross-filter rule narrows wood domain by
	// current metal and vice versa, so SKUs like (Pine, Green) — the only
	// valid pair for either material on this table — would otherwise be
	// rejected because each one-step write lands in a domain that's been
	// filtered against the *other* material's current (default) value.
	const opts = tableOptions[productType];
	configurator.set(root.tableMaterialMetal, {
		value: metal,
		domain: opts.metal,
	});
	if (wood !== undefined) {
		configurator.set(root.tableMaterialWood, {
			value: wood,
			domain: opts.wood,
		});
	}
	// chairConfiguration's run() hides chair[0..N] when viewerMode === "single-table".
}

export function grythyttanSetupProductFromArticleNumber(
	articleNumber: string,
	configurator: Configurator,
): string | undefined {
	const combo = lookupSkuCombo(articleNumber);
	if (!combo) {
		return `Unknown article number "${articleNumber}". The product is not in the catalog.`;
	}

	const { productType, wood, metal } = combo;

	if (isTable(productType)) {
		setTable(configurator, productType, wood, metal);
		return undefined;
	}

	const viewerMode = viewerModeForSeating(productType as SeatingType);
	setSeating(configurator, productType as SeatingType, wood, metal, viewerMode);
	return undefined;
}

// Type-only re-exports help downstream tests pin imports without round-tripping
// through `src/definitions`.
export type { BenchType, ChairType, SofaType };
